import * as React from 'react';
// import { useState } from 'react';
// import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Mousewheel } from 'swiper';
import { MARK_LINK, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import FancyTitle from './fancy-title';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/virtual';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css/effect-cards';
import LinkResolver from '../helpers/link-resolver';

function History({ title, subtitle, items }) {
  const [swiper, setSwiper] = React.useState(null);
  const [currentHeight, setCurrentHeight] = React.useState(0);
  const previousHeightRef = React.useRef(0);

  const matchHeightHistoryItem = React.useCallback(() => {
    if (typeof window === 'undefined') return;

    const slideItems = document.getElementsByClassName('history-slide-item');
    
    // Get all heights including hidden slides
    const heights = Array.from(slideItems).map(item => {
      // Temporarily make hidden slides visible to measure
      const wasHidden = item.style.display === 'none';
      if (wasHidden) {
        item.style.display = 'flex';
      }
      
      // Store original height
      const height = item.scrollHeight;
      
      // Restore hidden state
      if (wasHidden) {
        item.style.display = 'none';
      }
      
      return height;
    });

    const tallest = Math.max(...heights);
    
    // Only update if the new height is significantly different
    if (tallest > 0 && Math.abs(tallest - previousHeightRef.current) > 5) {
      previousHeightRef.current = tallest;
      setCurrentHeight(tallest);
      
      // Update Swiper after setting heights
      if (swiper) {
        swiper.update();
      }
    }
  }, [swiper]);

  // Run on mount and when items/content changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const timer = setTimeout(() => {
      matchHeightHistoryItem();
    }, 500);

    const handleResize = () => {
      matchHeightHistoryItem();
    };
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(() => {
      matchHeightHistoryItem();
    });

    const slideContainer = document.querySelector('.history-card-slider');
    if (slideContainer) {
      observer.observe(slideContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [matchHeightHistoryItem, items]);

  // Run when images load
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const images = document.querySelectorAll('.history-slide-item img');
    let loadedImages = 0;

    const imageLoadHandler = () => {
      loadedImages += 1;
      if (loadedImages === images.length) {
        matchHeightHistoryItem();
      }
    };

    images.forEach(img => {
      if (img.complete) {
        imageLoadHandler();
      } else {
        img.addEventListener('load', imageLoadHandler);
      }
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('load', imageLoadHandler);
      });
    };
  }, [matchHeightHistoryItem, items]);

  // Add CSS for smooth transitions
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const style = document.createElement('style');
    style.textContent = `
      .history-slide-item {
        transition: height 0.3s ease-in-out !important;
        min-height: ${currentHeight}px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [currentHeight]);

  return (
    <>
      <div className="lg:text-center flex flex-col lg:pr-16 py-8 lg:py-0 mt-[100px] lg:mt-60 overflow-x-hidden">
        {title && (
          <FancyTitle
            text={title}
            subtitle={subtitle}
            marginBottom="mb-20"
            textAlign="text-left"
            className="lg:w-fit lg:mx-auto"
            tag="h2"
          />
        )}
      </div>
      <div className="overflow-x-hidden">
        <Swiper
          modules={[EffectCards, Mousewheel]}
          className="history-card-slider"
          effect="cards"
          grabCursor
          initialSlide={0}
          slidesPerView={1}
          loop
          speed={500}
          mousewheel={{
            invert: false,
          }}
          autoHeight={false}
          onSwiper={setSwiper}
          onSlideChangeTransitionStart={() => {
            // Maintain current height during transition
            const slides = document.getElementsByClassName('history-slide-item');
            Array.from(slides).forEach(slide => {
              slide.style.minHeight = `${currentHeight}px`;
            });
          }}
          onSlideChangeTransitionEnd={() => {
            // Update height after transition
            setTimeout(matchHeightHistoryItem, 50);
          }}
        >
          {items && items.map((item, index) => (
            <SwiperSlide
              key={item.year}
              virtualIndex={index}
              className="lg:max-h-[800px] rounded border-2 rounded border-latori-green-dark flex justify-center items-start p-3 md:p-5 history-slide-item"
              style={{ minHeight: currentHeight }}
            >
              <div>
                <img
                  src={item.image.filename}
                  alt={item.title}
                  className={`w-full xl:col-span-2${
                    item.image_light ? ' hidden dark:inline-block' : ''
                  }`}
                  onLoad={matchHeightHistoryItem}
                />
                {item.title && (
                  <FancyTitle
                    text={item.title}
                    className="text-start mb-[16px] lg:mb-5 mt-[16px] lg:mt-5"
                    tag="h2"
                  />
                )}
                {item.text && (
                  <div>
                    {render(item.text, {
                      markResolvers: {
                        [MARK_LINK]: LinkResolver,
                      },
                      nodeResolvers: {
                        [NODE_PARAGRAPH]: (children) => (
                          <p className="first-of-type:mt-0 last-of-type:mb-0 m-auto text-sm md:text-base">
                            {children}
                          </p>
                        ),
                      },
                    })}
                  </div>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
}

export default History;
